import {
  AvatarDropdown,
  AvatarName,
  Footer,
  InternalNoticeAction,
  Question,
  SelectLang,
} from '@/components';
import { currentAccount as queryCurrentAccount } from '@/services/auth/api';
import { getEmployeeListInit } from '@/services/human-resources/employee-list/api';
import type { EmployeeListItem } from '@/services/human-resources/employee-list/types';
import { getProfileByAccountId } from '@/services/system-settings/profile/api';
import { isProfileComplete } from '@/utils/profileCompletion';
import type { MenuDataItem, Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { RequestInterceptor } from '@umijs/max';
import ReleaseNoticeModal from '@/components/ReleaseNoticeModal';
import { shouldShowInUndefinedEmployeeList } from '@/pages/HumanResources/EmployeeListShared/undefinedEmployeeFilters';
import GamificationHeartbeat from '@/components/GamificationHeartbeat';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const profilePath = '/profile';
const initPath = '/';
const undefinedEmployeeListPath = '/human-resources/employee-list/undefined';
const humanResourcesPath = '/human-resources';
const documentRequestsPath = '/human-resources/document-requests';
const documentRequestManagementPath = '/human-resources/document-requests/management';

const resolveDocumentRequestSelectedKeys = (pathname: string): string[] | undefined => {
  if (pathname === documentRequestManagementPath) {
    return [humanResourcesPath, documentRequestManagementPath];
  }
  if (pathname === documentRequestsPath) {
    return [humanResourcesPath, documentRequestsPath];
  }
  return undefined;
};

const authHeaderInterceptor: RequestInterceptor = (url, options) => {
  return { url, options };
};

const filterUndefinedEmployeeMenu = (
  menuData: MenuDataItem[] = [],
  showUndefinedEmployeeList?: boolean,
): MenuDataItem[] =>
  menuData
    .filter((item) => item.path !== undefinedEmployeeListPath || showUndefinedEmployeeList)
    .map((item) => ({
      ...item,
      children: item.children
        ? filterUndefinedEmployeeMenu(item.children, showUndefinedEmployeeList)
        : item.children,
    }));

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentAccount?: API.CurrentAccount;
  loading?: boolean;
  profileCompleted?: boolean;
  hasUndefinedEmployeeList?: boolean;
  fetchAccountInfo?: () => Promise<API.CurrentAccount | undefined>;
  fetchProfileCompleted?: (accountId?: string | number) => Promise<boolean>;
  fetchHasUndefinedEmployeeList?: () => Promise<boolean>;
}> {
  const fetchAccountInfo = async () => {
    try {
      const msg = await queryCurrentAccount({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const fetchProfileCompleted = async (accountId?: string | number) => {
    const id = Number(accountId);
    if (!id) return false;
    try {
      const profileResult = await getProfileByAccountId(
        { accountId: id },
        { skipErrorHandler: true },
      );
      return isProfileComplete(profileResult?.data);
    } catch {
      return false;
    }
  };

  const fetchHasUndefinedEmployeeList = async () => {
    try {
      const employeeListResult = await getEmployeeListInit(undefined, {
        skipErrorHandler: true,
      });
      const allList: EmployeeListItem[] = Array.isArray(employeeListResult?.data)
        ? employeeListResult.data
        : (employeeListResult?.data?.data ?? []);
      return allList.some(shouldShowInUndefinedEmployeeList);
    } catch {
      return false;
    }
  };
  const { location } = history;
  // 未输入url的情况，不执行fetchAccountInfo,防止没有token报错，直接跳转login
  if (location.pathname === initPath) {
    history.push(loginPath);
    return {
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  // 如果不是登录页面，执行
  if (location.pathname !== loginPath) {
    const currentAccount = await fetchAccountInfo();
    const profileCompleted = await fetchProfileCompleted(currentAccount?.accountId);
    const hasHr102InitView = currentAccount?.permissions?.includes('cbbm_hr102:init:view') ?? false;
    const hasUndefinedEmployeeList = hasHr102InitView
      ? await fetchHasUndefinedEmployeeList()
      : false;
    if (currentAccount && !profileCompleted && location.pathname !== profilePath) {
      history.push(profilePath);
    }
    return {
      fetchAccountInfo,
      fetchProfileCompleted,
      fetchHasUndefinedEmployeeList,
      currentAccount,
      profileCompleted,
      hasUndefinedEmployeeList,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchAccountInfo,
    fetchProfileCompleted,
    fetchHasUndefinedEmployeeList,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const selectedKeys = resolveDocumentRequestSelectedKeys(history.location.pathname);

  return {
    ...(selectedKeys ? { selectedKeys } : {}),
    // 自定义标题区域：只显示LOGO，不显示文字
    headerTitleRender: (logo, title, _) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 64,
          paddingLeft: 8,
        }}
      >
        {/* 调整LOGO大小 */}
        <span style={{ fontSize: 32, lineHeight: 1 }}>
          {React.isValidElement(logo)
            ? React.cloneElement(logo as React.ReactElement<{ style?: object }>, {
                style: { height: 38, width: 'auto' },
              })
            : logo}
        </span>
        {title && <span style={{ display: 'none' }}>{title}</span>}
      </div>
    ),
    actionsRender: () => [
      <InternalNoticeAction key="internalNoticeAction" />,
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentAccount?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // 水印（暂时不需要）
    waterMarkProps: {
      // content: initialState?.currentAccount?.accountName,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentAccount && location.pathname !== loginPath) {
        history.push(loginPath);
        return;
      }

      if (
        initialState?.currentAccount &&
        initialState.profileCompleted === false &&
        location.pathname !== profilePath
      ) {
        history.push(profilePath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuDataRender: (menuData) =>
      filterUndefinedEmployeeMenu(menuData, initialState?.hasUndefinedEmployeeList),
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          <ReleaseNoticeModal
            enabled={!!initialState?.currentAccount && initialState.profileCompleted !== false}
          />
          <GamificationHeartbeat
            enabled={!!initialState?.currentAccount && initialState.profileCompleted !== false}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
  requestInterceptors: [authHeaderInterceptor],
};
